import { API_BASE_PATH } from '../apiConfig';
import { recordScanAllowance } from './scanAllowance';

type StepId = 'agents' | 'skills' | 'codebase' | 'pricing';
type LoadState = 'idle' | 'loading' | 'ready' | 'error';
type LaunchState = 'idle' | 'submitting' | 'accepted' | 'error';
type PricingTier = 'free';
type ScanTier = 'free' | 'advanced';
type ScanType = 'smart-contract' | 'web';
type CodebaseSourceMode = 'upload';

const MAX_CODEBASE_ZIP_SIZE_BYTES = 35 * 1024 * 1024;

const getCodebaseZipValidationError = (file: File): string => {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return 'Select a .zip archive.';
  }

  if (file.size > MAX_CODEBASE_ZIP_SIZE_BYTES) {
    return 'The ZIP file is too large. Maximum size is 35 MB.';
  }

  return '';
};

interface AgentDiscoveryResponse {
  skills?: unknown;
  skillGroups?: unknown;
  webApiSkills?: unknown;
  aiAgents?: unknown;
  aiAgentModels?: unknown;
  agents?: unknown;
}

interface ScanAcceptedResponse {
  nextScanAt?: number;
  message?: string;
  runId?: string;
  scanTier?: ScanTier;
  paymentStatus?: 'not-required' | 'succeeded' | 'retry-credit' | 'required' | 'requires-action' | 'failed' | 'unavailable';
  paymentIntentId?: string;
  clientSecret?: string;
  amountCents?: number;
  currency?: 'usd';
  pricingPlan?: 'full' | 'light';
  status?: string;
  runDirectory?: string;
  uploadedCodebase?: {
    fileName?: string;
    sizeBytes?: number;
  };
  selectedBranch?: string;
  scopePath?: string;
  scopePaths?: string[];
  preconditionsPath?: string;
  resultEmail?: string;
  selectedSkills?: string[];
  selectedAiAgents?: string[];
  selectedAiAgentModels?: { aiAgent: string; model?: string }[];
  error?: string;
}

interface ScanAllowanceResponse {
  canScan: boolean;
  nextScanAt?: number;
  retryAfterSeconds?: number;
}

type AgentModelsByAgent = Record<string, string[]>;

type PricingProvider = 'openai' | 'anthropic' | 'zai' | 'unknown';
type PricingPlan = 'full' | 'light' | 'free';

interface PricingProviderModelCost {
  aiAgent: string;
  model: string;
  provider: PricingProvider;
  scanRunCount: number;
  estimatedInputTokens: number;
  inputTokenRateUsdPerMillion: number;
  apiCostUsd: number;
}

interface PricingCodebaseMetrics {
  scopePath?: string;
  scopePaths?: string[];
  fileCount: number;
  countedFileCount: number;
  totalBytes: number;
  nonCommentLineCount: number;
}

interface PricingTokenEstimate {
  baselineFullScanInputTokens: number;
  baselineSkillRunCount: number;
  baselineCodebaseTokens: number;
  estimatedCodebaseTokens: number;
  codebaseSizeMultiplier: number;
  averageInputTokensPerRun: number;
  totalEstimatedInputTokens: number;
}

interface PricingPlanEstimate {
  apiCostUsd: number;
  platformFeeUsd: number;
  totalUsd: number;
  selectedSkillCount: number;
  selectedModelCount: number;
  scanRunCount: number;
  selectedSkills?: string[];
  tokenEstimate: PricingTokenEstimate;
  providerModelCosts: PricingProviderModelCost[];
  selectedAiAgentModels?: { aiAgent: string; model?: string }[];
}

// The top-level fields describe the full plan; `plans` carries every plan for the same codebase.
interface PricingEstimateResponse extends PricingPlanEstimate {
  pricingType: 'smart-contract' | 'web-api';
  currency: 'USD';
  codebase: PricingCodebaseMetrics;
  plans?: Record<PricingPlan, PricingPlanEstimate>;
}

interface SelectedAiModel {
  agent: string;
  model: string;
}

interface SelectedAiAgentModels {
  agent: string;
  models: string[];
}

interface ScanWorkflowDraft {
  selectedScanType: ScanType | null;
  currentStep: StepId;
  selectedSkills: string[];
  selectedAiModels: string[];
  codebaseSourceMode: CodebaseSourceMode;
  uploadedCodebaseFileName: string;
  scopePath: string;
  scopePaths: string[];
  questionnaire: {
    nonStandardTokens: 'yes' | 'no' | '';
    trustedRoles: string;
    regularFunctions: string;
    adminRestrictions: string;
    assumptionsFilePath: string;
    externalContractsTrusted: 'yes' | 'no' | '';
  };
  selectedPricingTier: PricingTier | null;
  discountCode: string;
}

interface SkillInfo {
  title: string;
  description: string;
  sourceUrl: string;
}

const SCAN_TIER_PATHS: Record<ScanTier, string> = {
  free: '/scan/free',
  advanced: '/scan/advanced'
};
// The GitHub free scan is limited per signed-in user and repository by the backend, not by browser fingerprint.
const FREE_GITHUB_SCAN_PATH = '/scan/free/github';
// The daily allowance and its fingerprint headers only apply to the anonymous upload scan.
const FREE_SCAN_PATHS = new Set([SCAN_TIER_PATHS.free, '/scan', '/scan/allowance']);

const SCAN_DRAFT_STORAGE_KEY = 'scas-scan-draft';
const SCAN_LAUNCH_LOADING_MIN_MS = 10000;
const SCAN_BROWSER_FINGERPRINT_HEADER = 'X-Havu-Browser-Fingerprint';
const SCAN_RESULT_EMAIL_HEADER = 'X-Havu-Result-Email';
const INITIAL_STEP: StepId = 'agents';

const STEPS: { id: StepId; label: string }[] = [
  { id: 'agents', label: 'AI Agents' },
  { id: 'skills', label: 'Skills' },
  { id: 'codebase', label: 'Codebase' },
  { id: 'pricing', label: 'Pricing' }
];

const SKILL_LABELS: Record<string, string> = {
  pashov: 'Pashov',
  'sc-auditor': 'SC Auditor',
  quillshield: 'QuillShield',
  cyfrin: 'Cyfrin',
  'trail-of-bits': 'Trail of Bits',
  'trail-of-bits-supply-chain': 'Trail of bits',
  'trail-of-bits-static-analysis': 'Trail of bits',
  'semgrep-code-security': 'Semgrep',
  forefy: 'Forefy'
};

const SKILL_INFO: Record<string, SkillInfo> = {
  pashov: {
    title: 'Pashov',
    description: 'Professional smart contract security auditing skills and best practices from experienced auditor Pashov.',
    sourceUrl: 'https://github.com/pashov/skills'
  },
  'sc-auditor': {
    title: 'SC Auditor',
    description: 'Comprehensive smart contract auditing toolkit with vulnerability patterns and security checks.',
    sourceUrl: 'https://github.com/Archethect/sc-auditor'
  },
  quillshield: {
    title: 'QuillShield',
    description: 'Advanced security analysis skills from QuillAI Network for smart contract vulnerability detection.',
    sourceUrl: 'https://github.com/quillai-network/quillshield_skills'
  },
  cyfrin: {
    title: 'Cyfrin',
    description: 'Solskill by Cyfrin - Modern smart contract security scanning and auditing methodologies.',
    sourceUrl: 'https://github.com/Cyfrin/solskill'
  },
  'trail-of-bits': {
    title: 'Trail of Bits',
    description: 'Industry-leading security research skills from Trail of Bits security experts.',
    sourceUrl: 'https://github.com/trailofbits/skills'
  },
  forefy: {
    title: 'Forefy',
    description: 'Specialized smart contract audit context and security assessment methodologies.',
    sourceUrl: 'https://github.com/forefy/.context/blob/main/skills/smart-contract-audit/SKILL.md'
  }
};

const AI_AGENT_LABELS: Record<string, string> = {
  codex: 'Codex',
  claude: 'Claude',
  opencode: 'OpenCode'
};

const FALLBACK_AGENT_MODELS: AgentModelsByAgent = {
  codex: ['gpt-daybreak-blue-latest'],
  claude: ['claude-opus-5']
};

const PRICING_TIER_LABELS: Record<PricingTier, string> = {
  free: 'Free'
};

const isStepId = (value: unknown): value is StepId => {
  return typeof value === 'string' && STEPS.some((step) => step.id === value);
};

const isPricingTier = (value: unknown): value is PricingTier => {
  return value === 'free';
};

const isScanType = (value: unknown): value is ScanType => {
  return value === 'smart-contract' || value === 'web';
};

const isCodebaseSourceMode = (value: unknown): value is CodebaseSourceMode => {
  return value === 'upload';
};

const normalizeList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(new Set(
    value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)
  ));
};

const compactScopePaths = (scopePaths: string[]): string[] => {
  const uniqueScopes = Array.from(new Set(scopePaths
    .map((scopePath) => scopePath.trim().replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right));

  return uniqueScopes.filter((scopePath, index) => {
    return !uniqueScopes.some((candidate, candidateIndex) => {
      return candidateIndex !== index && scopePath.startsWith(`${candidate}/`);
    });
  });
};

const hasAncestorScopePath = (scopePaths: string[], scopePath: string): boolean => {
  return scopePaths.some((candidate) => scopePath.startsWith(`${candidate}/`));
};

const isScopePathIncluded = (scopePaths: string[], scopePath: string): boolean => {
  return scopePaths.includes(scopePath) || hasAncestorScopePath(scopePaths, scopePath);
};

const formatScopePaths = (scopePaths: string[]): string => {
  if (scopePaths.length === 0) {
    return 'Full codebase';
  }

  if (scopePaths.length <= 2) {
    return scopePaths.join(', ');
  }

  return `${scopePaths.length} folders`;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const normalizeAgentModels = (value: unknown, availableAgents: string[]): AgentModelsByAgent => {
  const modelsByAgent: AgentModelsByAgent = {};

  if (isRecord(value)) {
    for (const agent of availableAgents) {
      const models = normalizeList(value[agent]);

      if (models.length > 0) {
        modelsByAgent[agent] = models;
      }
    }
  }

  return modelsByAgent;
};

const normalizeAgentInfoModels = (value: unknown): AgentModelsByAgent => {
  if (!Array.isArray(value)) {
    return {};
  }

  return value.reduce<AgentModelsByAgent>((modelsByAgent, item) => {
    if (!isRecord(item)) {
      return modelsByAgent;
    }

    const name = typeof item.name === 'string' ? item.name.trim() : '';
    const models = normalizeList(item.models);

    if (name && models.length > 0) {
      modelsByAgent[name] = models;
    }

    return modelsByAgent;
  }, {});
};

const getDiscoveryAgentModels = (discovery: AgentDiscoveryResponse, availableAgents: string[]): AgentModelsByAgent => {
  const fromMap = normalizeAgentModels(discovery.aiAgentModels, availableAgents);
  const fromAgentInfo = normalizeAgentInfoModels(discovery.agents);
  const modelsByAgent: AgentModelsByAgent = {};

  for (const agent of availableAgents) {
    const models = fromMap[agent] ?? fromAgentInfo[agent] ?? FALLBACK_AGENT_MODELS[agent] ?? [];

    modelsByAgent[agent] = models;
  }

  return modelsByAgent;
};

const getAiModelKey = (agent: string, model: string): string => `${agent}\n${model}`;

const getDefaultAiModelKeys = (availableModelKeys: Set<string>): string[] => {
  const defaults = [
    getAiModelKey('codex', 'gpt-daybreak-blue-latest'),
    ...Array.from(availableModelKeys).filter((key) => key.startsWith('claude\n'))
  ]
    .filter((key) => availableModelKeys.has(key));

  return defaults.length > 0 ? defaults : Array.from(availableModelKeys);
};

const getOrderedSelectedAiModels = (
  availableAiAgents: string[],
  agentModelsByAgent: AgentModelsByAgent,
  selectedAiModels: Set<string>
): SelectedAiModel[] => {
  return availableAiAgents.flatMap<SelectedAiModel>((agent) => {
    const models = agentModelsByAgent[agent] ?? [];

    return models
      .filter((model) => selectedAiModels.has(getAiModelKey(agent, model)))
      .map((model) => ({ agent, model }));
  });
};

const getOrderedSelectedAiAgents = (orderedSelectedAiModels: SelectedAiModel[]): string[] => {
  return Array.from(new Set(orderedSelectedAiModels.map(({ agent }) => agent)));
};

const getSelectedAiAgentsWithModels = (orderedSelectedAiModels: SelectedAiModel[]): SelectedAiAgentModels[] => {
  const modelsByAgent = orderedSelectedAiModels.reduce<Record<string, string[]>>((grouped, selection) => {
    grouped[selection.agent] = [...(grouped[selection.agent] ?? []), selection.model];

    return grouped;
  }, {});

  return getOrderedSelectedAiAgents(orderedSelectedAiModels).map((agent) => ({
    agent,
    models: modelsByAgent[agent] ?? []
  }));
};

const getNextWorkflowStep = (currentStep: StepId): StepId => {
  const currentIndex = STEPS.findIndex((step) => step.id === currentStep);
  const nextStep = STEPS[currentIndex + 1];

  return nextStep?.id ?? currentStep;
};

const getPreviousWorkflowStep = (currentStep: StepId): StepId => {
  const currentIndex = STEPS.findIndex((step) => step.id === currentStep);
  const previousStep = STEPS[currentIndex - 1];

  return previousStep?.id ?? currentStep;
};

const formatModelLabel = (model: string): string => {
  // opencode model ids are provider-qualified, for example openrouter/z-ai/glm-5.3.
  const trimmed = model.trim().slice(model.trim().lastIndexOf('/') + 1);
  const normalized = trimmed.toLowerCase();

  if (normalized === 'gpt-daybreak-blue-latest') {
    return 'Daybreak Blue';
  }

  if (normalized.startsWith('glm-')) {
    return `GLM-${trimmed.slice(4)}`;
  }

  if (normalized.startsWith('gpt-')) {
    const [version = '', ...variantParts] = trimmed.slice(4).split('-').filter(Boolean);
    const variant = variantParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    return variant ? `GPT-${version} ${variant}` : `GPT-${version}`;
  }

  if (normalized.startsWith('claude-')) {
    return normalized
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return trimmed;
};

const formatLabel = (value: string, labels: Record<string, string>): string => {
  if (labels[value]) {
    return labels[value];
  }

  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };

    if (typeof error === 'string' && error.trim()) {
      return error;
    }
  }

  return `Request failed with status ${fallbackStatus}`;
};

const normalizeFingerprintHeaderPart = (value: unknown): string => {
  return typeof value === 'string'
    ? value.trim().replace(/[^\x20-\x7E]/g, '').replace(/[|\r\n]+/g, ' ').slice(0, 120)
    : '';
};

const getScreenBucket = (): string => {
  const width = Math.round((window.screen?.width || 0) / 200) * 200;
  const height = Math.round((window.screen?.height || 0) / 200) * 200;

  return width && height ? `${width}x${height}` : '';
};

const getScanBrowserFingerprint = (): string => {
  return [
    'v1',
    normalizeFingerprintHeaderPart(window.navigator.userAgent),
    normalizeFingerprintHeaderPart(window.navigator.language),
    normalizeFingerprintHeaderPart(Intl.DateTimeFormat().resolvedOptions().timeZone),
    getScreenBucket()
  ]
    .filter(Boolean)
    .join('|')
    .slice(0, 512);
};

const getScanResultEmailFromBody = (body: BodyInit | null | undefined): string => {
  let rawPayload: unknown;

  if (body instanceof FormData) {
    rawPayload = body.get('payload');
  } else if (typeof body === 'string') {
    rawPayload = body;
  }

  if (typeof rawPayload !== 'string') {
    return '';
  }

  try {
    const payload = JSON.parse(rawPayload) as unknown;

    return isRecord(payload) && typeof payload.resultEmail === 'string'
      ? payload.resultEmail.trim()
      : '';
  } catch {
    return '';
  }
};

const requestJson = async <ResponseType,>(path: string, init?: RequestInit): Promise<ResponseType> => {
  const headers = new Headers(init?.headers);

  if (typeof init?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (FREE_SCAN_PATHS.has(path)) {
    headers.set(SCAN_BROWSER_FINGERPRINT_HEADER, getScanBrowserFingerprint());

    const resultEmail = getScanResultEmailFromBody(init?.body);

    if (resultEmail) {
      headers.set(SCAN_RESULT_EMAIL_HEADER, resultEmail);
    }
  }

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    headers,
    credentials: 'include'
  });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (FREE_SCAN_PATHS.has(path) && (response.ok || response.status === 429)) {
    const canScan = payload && typeof payload === 'object' && 'canScan' in payload
      ? (payload as { canScan?: unknown }).canScan
      : undefined;

    recordScanAllowance(
      canScan === true
        ? undefined
        : payload && typeof payload === 'object' && 'nextScanAt' in payload
        ? (payload as { nextScanAt: unknown }).nextScanAt
        : undefined
    );
  }

  if (!response.ok) {
    throw new ApiRequestError(getErrorMessage(payload, response.status), response.status, payload);
  }

  return payload as ResponseType;
};

class ApiRequestError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.payload = payload;
  }
}

const requestScanAllowance = async (scanPayload: Record<string, unknown> = {}): Promise<ScanAllowanceResponse> => {
  return requestJson<ScanAllowanceResponse>('/scan/allowance', {
    method: 'POST',
    body: JSON.stringify(scanPayload)
  });
};

/** Free scan: email plus an uploaded codebase, limited to one run per day and per email. */
const requestFreeScan = async (formData: FormData): Promise<ScanAcceptedResponse> => {
  return requestJson<ScanAcceptedResponse>(SCAN_TIER_PATHS.free, {
    method: 'POST',
    body: formData
  });
};

/** Free GitHub scan: signed-in only, limited to one run per user and repository per day. */
const requestFreeGitHubScan = async (scanPayload: Record<string, unknown>): Promise<ScanAcceptedResponse> => {
  return requestJson<ScanAcceptedResponse>(FREE_GITHUB_SCAN_PATH, {
    method: 'POST',
    body: JSON.stringify(scanPayload)
  });
};

/** Whether the signed-in user can still run today's free scan on the repository in `scanPayload`. */
const requestFreeGitHubScanAllowance = async (scanPayload: Record<string, unknown>): Promise<ScanAllowanceResponse> => {
  return requestJson<ScanAllowanceResponse>(`${FREE_GITHUB_SCAN_PATH}/allowance`, {
    method: 'POST',
    body: JSON.stringify(scanPayload)
  });
};

/** Advanced scan: returns structured Stripe states so the caller can complete 3-D Secure. */
const requestAdvancedScan = async (scanPayload: Record<string, unknown>): Promise<ScanAcceptedResponse> => {
  try {
    return await requestJson<ScanAcceptedResponse>(SCAN_TIER_PATHS.advanced, {
      method: 'POST',
      body: JSON.stringify(scanPayload)
    });
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && isRecord(error.payload)) {
      const paymentStatus = error.payload.paymentStatus;

      if (typeof paymentStatus === 'string') {
        return error.payload as ScanAcceptedResponse;
      }
    }

    throw error;
  }
};

const getRouteRunId = (pathname = window.location.pathname): string | null => {
  const match = pathname.match(/^\/run\/([^/]+)\/?$/);

  if (!match) {
    return null;
  }

  return decodeURIComponent(match[1]);
};

const normalizeStoredYesNo = (value: unknown): 'yes' | 'no' | '' => {
  return value === 'yes' || value === 'no' ? value : '';
};

const normalizeStoredText = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const normalizeStoredScopePaths = (value: unknown, fallbackScopePath: string): string[] => {
  const scopePaths = normalizeList(value);

  return scopePaths.length > 0
    ? scopePaths
    : fallbackScopePath ? [fallbackScopePath] : [];
};

const getStoredScanDraft = (): ScanWorkflowDraft | null => {
  const rawValue = window.localStorage.getItem(SCAN_DRAFT_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const value = JSON.parse(rawValue) as unknown;

    if (!isRecord(value)) {
      window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
      return null;
    }

    const questionnaire = isRecord(value.questionnaire) ? value.questionnaire : {};

    const scopePath = normalizeStoredText(value.scopePath);

    return {
      selectedScanType: isScanType(value.selectedScanType) ? value.selectedScanType : null,
      currentStep: isStepId(value.currentStep) ? value.currentStep : INITIAL_STEP,
      selectedSkills: normalizeList(value.selectedSkills),
      selectedAiModels: normalizeList(value.selectedAiModels),
      codebaseSourceMode: isCodebaseSourceMode(value.codebaseSourceMode) ? value.codebaseSourceMode : 'upload',
      uploadedCodebaseFileName: normalizeStoredText(value.uploadedCodebaseFileName),
      scopePath,
      scopePaths: normalizeStoredScopePaths(value.scopePaths, scopePath),
      questionnaire: {
        nonStandardTokens: normalizeStoredYesNo(questionnaire.nonStandardTokens),
        trustedRoles: normalizeStoredText(questionnaire.trustedRoles),
        regularFunctions: normalizeStoredText(questionnaire.regularFunctions),
        adminRestrictions: normalizeStoredText(questionnaire.adminRestrictions),
        assumptionsFilePath: normalizeStoredText(questionnaire.assumptionsFilePath),
        externalContractsTrusted: normalizeStoredYesNo(questionnaire.externalContractsTrusted)
      },
      selectedPricingTier: isPricingTier(value.selectedPricingTier) ? value.selectedPricingTier : null,
      discountCode: normalizeStoredText(value.discountCode)
    };
  } catch {
    window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
    return null;
  }
};

const storeScanDraft = (draft: ScanWorkflowDraft): void => {
  window.localStorage.setItem(SCAN_DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

const clearScanDraft = (): void => {
  window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
};

const isPricingTierAvailable = (pricingTier: PricingTier | null): boolean => {
  if (!pricingTier) {
    return false;
  }

  return isPricingTier(pricingTier);
};

const getPricingTierLabel = (pricingTier: PricingTier | null): string => {
  return pricingTier ? PRICING_TIER_LABELS[pricingTier] : '';
};

const waitForMinimumScanLaunchLoading = async (startedAtMs: number): Promise<void> => {
  const elapsedMs = Date.now() - startedAtMs;
  const remainingMs = Math.max(0, SCAN_LAUNCH_LOADING_MIN_MS - elapsedMs);

  if (remainingMs > 0) {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, remainingMs);
    });
  }
};

export {
  AI_AGENT_LABELS,
  ApiRequestError,
  INITIAL_STEP,
  SCAN_TIER_PATHS,
  SCAN_LAUNCH_LOADING_MIN_MS,
  SKILL_INFO,
  SKILL_LABELS,
  STEPS,
  clearScanDraft,
  compactScopePaths,
  formatLabel,
  formatModelLabel,
  formatScopePaths,
  getAiModelKey,
  getCodebaseZipValidationError,
  getDefaultAiModelKeys,
  getDiscoveryAgentModels,
  getNextWorkflowStep,
  getOrderedSelectedAiAgents,
  getOrderedSelectedAiModels,
  getPreviousWorkflowStep,
  getPricingTierLabel,
  getRouteRunId,
  getSelectedAiAgentsWithModels,
  getStoredScanDraft,
  isPricingTierAvailable,
  isRecord,
  isCodebaseSourceMode,
  isScopePathIncluded,
  isStepId,
  normalizeList,
  normalizeStoredText,
  requestAdvancedScan,
  requestFreeGitHubScan,
  requestFreeGitHubScanAllowance,
  requestFreeScan,
  requestJson,
  requestScanAllowance,
  storeScanDraft,
  waitForMinimumScanLaunchLoading
};

export type {
  AgentDiscoveryResponse,
  AgentModelsByAgent,
  CodebaseSourceMode,
  LaunchState,
  LoadState,
  PricingEstimateResponse,
  PricingPlan,
  PricingPlanEstimate,
  PricingProvider,
  PricingProviderModelCost,
  PricingTokenEstimate,
  PricingTier,
  ScanAcceptedResponse,
  ScanAllowanceResponse,
  ScanTier,
  ScanType,
  ScanWorkflowDraft,
  SelectedAiAgentModels,
  SelectedAiModel,
  SkillInfo,
  StepId
};
